import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { selectActivePage } from "../store/ui";
import { useTranslation } from "react-i18next";
import { sendAnalyticsEvents } from "../analytics/common";
import { EVENTS } from "../analytics/events";
import { selectEventsData, setEventData } from "../store/events";
import { useNavigate } from "react-router-dom";
import { setRole } from "../store/info";
import { selectLandingType } from "../store/plans";

const isArabic = navigator.language.startsWith("ar");
const textAlign = isArabic ? "right" : "left";

const StyledBarWrapper = styled.div`
  height: 8px;
  width: 100%;
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;

  @media screen and (min-width: 450px) {
    margin-top: 4vh;
    margin-bottom: 4vh;
  }
`;

const StyledBar = styled.div`
  min-height: 8px;
  width: 284px;
  height: 100%;
  display: flex;
`;

const SkipBtn = styled.div`
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font16};
  line-height: ${(props) => props.theme.lineHeights.lineHeight19};
  color: ${(props) => props.theme.colors.colorText};
  margin-${textAlign}: 26px;
  white-space: nowrap;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`

const StyledBarItem = styled.div`
  flex-grow: 1;
  background-color: ${({ isActive, theme }) =>
    isActive
      ? theme.colors.colorPrimary
      : theme.colors.colorInactiveProgressTrack};
  transition: background-color 300ms;
  :first-child {
    border-top-left-radius: 29px;
    border-bottom-left-radius: 29px;
  }
  :last-child {
    border-top-right-radius: 29px;
    border-bottom-right-radius: 29px;
  }
`;

const StyledCount = styled.div`
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font16};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  color: ${(props) => props.theme.colors.colorText};
  margin-left: 12px;
  letter-spacing: -0.08px;

  & > span {
    color: ${(props) => props.theme.colors.colorPrimary};
  }
`;

const AppProgressBar = () => {
  const activePage = useSelector(selectActivePage);
  const [showBar, setShowBar] = useState(false);
  const eventsData = useSelector(selectEventsData);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const landingType = useSelector(selectLandingType);
  const isIntroOffer = landingType === 'intro_offer';

  const pagesWithProgressBar = useMemo(() => {
    return landingType === 'trivia' ? [
      'trivia',
      'trivia-q2',
      'trivia-q3',
      'trivia-q4',
      'trivia-q5',
      'trivia-q6',
      'trivia-q7',
      'trivia-q8',
      'trivia-q9',
      'trivia-q10',
      'trivia-q11',
      'trivia-q12',
      'trivia-q13',
      'trivia-q14',
      'trivia-q15',
      'trivia-q16',
      'trivia-q17',
      'trivia-q18',
      'trivia-q19',
      'trivia-q20',
      'trivia-q21',
      'trivia-q22',
    ] : [
      "choose-target",
      "choose-location",
      "choose-reason",
      "choose-time",
      "choose-frequency",
      "choose-location-type",
      "choose-quantity",
      "choose-feature",
      "explore-solutions",
      "real-time-location",
      "place-alerts",
      "sos-alerts",
      "platform-support",
      !isIntroOffer && 'signup',
    ];
  }, [landingType, isIntroOffer]);

  const activePageIndex = useMemo(
    () => pagesWithProgressBar.indexOf(activePage),
    [activePage, pagesWithProgressBar]
  );

  useEffect(() => {
    setShowBar(activePageIndex >= 0);
  }, [activePageIndex]);

  const renderProgress = (item, i) => {
    const isActive = i <= activePageIndex;

    return <StyledBarItem isActive={isActive} key={i} />;
  };

  if (!showBar) return null;

  const pageTransitions = {
    "choose-target": {
      eventId: "onboardingRoleSkip",
      eventDataKey: "Role",
      nextPage: "/choose-location",
      setupRole: true
    },
    "choose-location": {
      eventId: "onboardingLocationsSkip",
      eventDataKey: "Locations",
      nextPage: "/choose-reason",
    },
    "choose-reason": {
      eventId: "onboardingReasonSkip",
      eventDataKey: "Reason",
      nextPage: "/choose-time",
    },
    "choose-time": {
      eventId: "onboardingCheckTimeSkip",
      eventDataKey: "Frequency",
      nextPage: "/choose-frequency",
    },
    "choose-frequency": {
      eventId: "onboardingUpdatesSkip",
      eventDataKey: "Updates",
      nextPage: "/choose-location-type",
    },
    "choose-location-type": {
      eventId: "onboardingInappropriateSkip",
      eventDataKey: "InappropriateGeos",
      nextPage: "/choose-quantity",
    },
    "choose-quantity": {
      eventId: "onboardingQuantitySkip",
      eventDataKey: "Quantity",
      nextPage: "/choose-feature",
    },
    "choose-feature": {
      eventId: "onboardingFeatureListSkip",
      eventDataKey: "Features",
      nextPage: "/explore-solutions",
    },
  };
  
  const handleSkipClick = () => {
    const pageTransition = pageTransitions[activePage];
  
    if (pageTransition) {
      const { eventId, eventDataKey, nextPage, setupRole } = pageTransition;
  
      dispatch(setEventData({ id: eventDataKey, data: 'Other' }));
      setupRole && dispatch(setRole('Other'));
      const updatedEventData = {
        ...eventsData,
        [eventDataKey]: 'Other',
      };
  
      sendAnalyticsEvents(EVENTS[eventId], updatedEventData);
      navigate(nextPage);
    }
  };

  return (
    <StyledBarWrapper>
      <StyledBar>{pagesWithProgressBar.map(renderProgress)}</StyledBar>
      {landingType === 'trivia' &&
        pagesWithProgressBar.map(
          (item, index) =>
            item === activePage && (
              <StyledCount key={index}>
                <span>{index + 1}</span>/22
              </StyledCount>
            )
        )}
      {pageTransitions[activePage] && <SkipBtn id='skip-button' onClick={() => handleSkipClick()}>{t('skip')}</SkipBtn>}
    </StyledBarWrapper>
  );
};

export default AppProgressBar;
